export { UserDetailPage } from './detail';
export type { RoleAssignment } from './utils';

import { List, useTable } from '@refinedev/antd';
import { useCan, useList, useParsed } from '@refinedev/core';
import { Table, Input, Tag, Space, Select, Alert, Button, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CrudFilters } from '@refinedev/core';
import type { AuthRole, RoleAssignment } from './utils';
import { renderRoleAssignments } from './renderRoleAssignments';
import { TEAM_OPTIONS, teamLabel } from '../../utils/teams';
import { UsersBatchBar } from './components/UsersBatchBar';
import { InfiniteCompanySelect } from './components/InfiniteCompanySelect';
import {
  COMPANY_FILTER_QUERY_KEY,
  useCompanyName,
  useFetchCompanyFilterPage,
} from './api/companyFilterClient';
import { ListTotal } from '../../components/table/ListTotal';
import {
  DEFAULT_GROUP,
  NO_ADMIN_APP_ACCOUNT_TITLE,
  hasNoAdminAppAccount,
  isUserGroup,
  type PlatformAccount,
  type UserGroup,
} from './accounts';

// The sign-in service reads `?group=`: ServeFirst people (staff and web app super admins, with
// or without an admin app account) or everyone else who signed in to the admin app.
const GROUP_TABS = [
  { key: 'servefirst', label: 'ServeFirst' },
  { key: 'platform', label: 'Platform' },
];
const GROUP_FILTER = { field: 'group', operator: 'eq', value: DEFAULT_GROUP } as const;

// The list shows active people unless someone picks another status. The sign-in service reads
// `?status=active|inactive` as the person (active = every account they have is on) and
// `?status=all` as every status. The default goes in the address like any other filter, so "All"
// has to be a value of its own, or a reload would bring the default back.
const DEFAULT_STATUS = 'active';
const STATUS_FILTER = { field: 'status', operator: 'eq', value: DEFAULT_STATUS } as const;
const STATUS_FILTER_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All statuses' },
];

// The sign-in service reads `?missing=adminApp|platform`, only on the ServeFirst tab.
const MISSING_ACCOUNT_OPTIONS = [
  { value: 'adminApp', label: 'No admin app account' },
  { value: 'platform', label: 'No web app account' },
];

const greyTag = (text: string) => <Tag color="default">{text}</Tag>;

const renderWebAppAccount = (account: PlatformAccount | null | undefined) => {
  if (!account) return greyTag('No account');
  if (account.active === false) return greyTag('Inactive');
  return account.kind === 'limited' ? (
    <Tag color="cyan">Limited super admin</Tag>
  ) : (
    <Tag color="blue">Full super admin</Tag>
  );
};

const PLATFORM_EMPTY_TEXT =
  "No platform users yet. Customers who sign in with their company's SSO show here.";
const PLATFORM_NO_ACTIVE_TEXT =
  'No active platform users. Pick All statuses to see switched-off ones too.';

// The sign-in service reads `?team=none` as "people with no team set".
const NO_TEAM_FILTER = 'none';
const TEAM_FILTER_OPTIONS = [...TEAM_OPTIONS, { value: NO_TEAM_FILTER, label: 'No team' }];

// The sign-in service reads `?superAdmin=limited|full` (SF-4825): web app platform super admins
// with a company list, or with none (every company).
const SUPER_ADMIN_FILTER_OPTIONS = [
  { value: 'limited', label: 'Limited super admins' },
  { value: 'full', label: 'Full super admins' },
];

// SF-4996: the sign-in service reads `?companyId=` as "people who can see this company in the web
// app" and then says for each one whether it is on their list or they see every company.
const ACCESS_LABELS: Record<string, string> = {
  list: 'Has this company',
  all: 'All companies',
};

// The filters `applyFilters` rebuilds from `ListFilters`; any other filter is left alone.
const MANAGED_FILTER_FIELDS = new Set([
  'group',
  'status',
  'missing',
  'search',
  'team',
  'superAdmin',
  'role',
  'companyId',
]);

interface ListFilters {
  group?: UserGroup;
  status?: string;
  missing?: string;
  search?: string;
  team?: string;
  superAdmin?: string;
  role?: string;
  companyId?: string;
}

// The sign-in service answers errors as { error: { code, message, status } }; for example a 502
// SUPER_ADMIN_LOOKUP_FAILED when it could not check who is a super admin.
const listErrorMessage = (error: unknown) => {
  const message = (error as { response?: { data?: { error?: { message?: unknown } } } })?.response
    ?.data?.error?.message;
  return typeof message === 'string' && message.trim() ? message : 'Please try again.';
};

interface User {
  id: string;
  _id?: string;
  name: string;
  email: string;
  roleAssignments?: RoleAssignment[];
  team?: string | null;
  status: string;
  createdAt: string;
  /** Only while the company filter is on (SF-4996). */
  superAdminAccess?: 'list' | 'all';
  /** false: a web app super admin who never signed in to the admin app. */
  adminAppAccount?: boolean;
  platformAccount?: PlatformAccount | null;
}

export const UsersListPage: React.FC = () => {
  const navigate = useNavigate();
  // SF-4875: ticked people, kept across pages and filter changes until cleared.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Name or email of each ticked person, for the batch failure list (they may be on another page).
  const selectedLabels = useRef(new Map<string, string>());

  // Same check as the team and role editors on the user detail page.
  const { data: canManageUsers } = useCan({ resource: 'users', action: 'edit' });

  // Every list request carries the tab and a status. A link from another page (a role's or a
  // company's count) carries its own filters but neither, and Refine then skips
  // `filters.initial`: wait for the effect below to add the ServeFirst tab and Active instead of
  // listing without them.
  const { params: urlParams } = useParsed();
  const urlFilters = (urlParams?.filters ?? []) as CrudFilters;
  const urlHas = (field: string) => urlFilters.some((f) => 'field' in f && f.field === field);
  const urlReady = urlFilters.length === 0 || (urlHas('group') && urlHas('status'));

  const {
    tableProps,
    tableQuery,
    filters: tableFilters,
    setFilters,
    setCurrent,
  } = useTable<User>({
    resource: 'users',
    meta: { dataProviderName: 'authDataProvider' },
    syncWithLocation: true,
    pagination: { pageSize: 10 },
    filters: { initial: [GROUP_FILTER, STATUS_FILTER] },
    queryOptions: { enabled: urlReady },
  });

  const tableHasGroup = tableFilters.some((f) => 'field' in f && f.field === 'group');
  const tableHasStatus = tableFilters.some((f) => 'field' in f && f.field === 'status');
  useEffect(() => {
    if (tableHasGroup && tableHasStatus) return;
    setFilters((prev) => {
      const of = (field: string) => prev.filter((f) => 'field' in f && f.field === field);
      const others = prev.filter(
        (f) => !('field' in f && (f.field === 'group' || f.field === 'status')),
      );
      const groupFilters = of('group');
      const statusFilters = of('status');
      return [
        ...(groupFilters.length ? groupFilters : [GROUP_FILTER]),
        ...(statusFilters.length ? statusFilters : [STATUS_FILTER]),
        ...others,
      ];
    });
  }, [tableHasGroup, tableHasStatus, setFilters]);

  const rolesQuery = useList<AuthRole>({
    resource: 'roles',
    pagination: { mode: 'off' },
    meta: { dataProviderName: 'authDataProvider' },
    queryOptions: { staleTime: 60_000 },
  });
  const rolesResult = rolesQuery.data;

  const roleScopeByName = new Map(
    (rolesResult?.data ?? []).map((role) => [role.name, role.scope] as const),
  );

  // Read the filters back from the table so values carried in the URL are kept and shown.
  const filterValue = (field: string) =>
    tableFilters.find((f) => 'field' in f && f.field === field)?.value as string | undefined;
  const groupValue = filterValue('group');

  // Start the search box from the table's own search. Back from a person's page brings the
  // search back from the address, and an empty box over a filtered list can't be cleared.
  const [searchValue, setSearchValue] = useState(() => filterValue('search') ?? '');
  const group: UserGroup = isUserGroup(groupValue) ? groupValue : DEFAULT_GROUP;
  const isServeFirst = group === 'servefirst';
  const currentFilters = (): ListFilters => ({
    group,
    status: filterValue('status'),
    missing: filterValue('missing'),
    search: filterValue('search'),
    team: filterValue('team'),
    superAdmin: filterValue('superAdmin'),
    role: filterValue('role'),
    companyId: filterValue('companyId'),
  });

  // Rebuild the whole list and replace: Refine's default merge never drops a filter we leave
  // out, so clearing the search would keep the old search applied. A narrower list can have
  // fewer pages than the one we are on, so go back to page 1 or matches stay hidden.
  // Each change passes only the filter it changes; the others are read back from the table.
  const applyFilters = (changed: ListFilters) => {
    const { group, status, missing, search, team, superAdmin, role, companyId } = {
      ...currentFilters(),
      ...changed,
    };
    // Filters this page has no control for are kept as they are; only the ones rebuilt below
    // are replaced.
    const untouched = tableFilters.filter(
      (f) => !('field' in f && MANAGED_FILTER_FIELDS.has(f.field)),
    );
    const filters: CrudFilters = [
      { ...GROUP_FILTER, value: group ?? DEFAULT_GROUP },
      { ...STATUS_FILTER, value: status ?? DEFAULT_STATUS },
    ];
    // The sign-in service only takes `missing` with the ServeFirst tab.
    if (missing && group === 'servefirst') {
      filters.push({ field: 'missing', operator: 'eq', value: missing });
    }
    if (search) filters.push({ field: 'search', operator: 'contains', value: search });
    if (team) filters.push({ field: 'team', operator: 'eq', value: team });
    if (superAdmin) filters.push({ field: 'superAdmin', operator: 'eq', value: superAdmin });
    // The sign-in service reads `?role=<role name>` as "people holding that role".
    if (role) filters.push({ field: 'role', operator: 'eq', value: role });
    if (companyId) filters.push({ field: 'companyId', operator: 'eq', value: companyId });
    filters.push(...untouched);
    setFilters(filters, 'replace');
    setCurrent(1);
  };

  const handleGroupChange = (key: string) => {
    if (isUserGroup(key)) applyFilters({ group: key });
  };

  const handleStatusChange = (value: string) => {
    applyFilters({ status: value });
  };

  const handleMissingChange = (value: string | undefined) => {
    applyFilters({ missing: value });
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    applyFilters({ search: value });
  };

  const handleTeamChange = (value: string | undefined) => {
    applyFilters({ team: value });
  };

  const handleSuperAdminChange = (value: string | undefined) => {
    applyFilters({ superAdmin: value });
  };

  const handleRoleChange = (value: string | undefined) => {
    applyFilters({ role: value });
  };

  const companyFilter = filterValue('companyId');
  const fetchCompanyPage = useFetchCompanyFilterPage();
  const company = useCompanyName(companyFilter);
  // The name of a company picked here, so it shows at once instead of waiting for the read.
  const pickedCompanyNames = useRef(new Map<string, string>());
  // Read the companies only once someone opens the picker, not on every visit to the page.
  const [companyPickerUsed, setCompanyPickerUsed] = useState(false);
  let companyLabel: string | undefined;
  if (companyFilter) {
    companyLabel =
      company.name ??
      pickedCompanyNames.current.get(companyFilter) ??
      (company.isLoading ? 'Loading company…' : companyFilter);
  }

  const handleCompanyChange = (choice?: { value: string; label?: React.ReactNode }) => {
    if (choice && typeof choice.label === 'string') {
      pickedCompanyNames.current.set(choice.value, choice.label);
    }
    applyFilters({ companyId: choice?.value });
  };

  // While the roles request has failed, offer nothing rather than a stale or empty list that
  // reads as "no roles"; the error and Retry show next to the filter.
  const rolesFailed = rolesQuery.isError;
  const roleFilterOptions = rolesFailed
    ? []
    : (rolesResult?.data ?? [])
        .map((role) => ({ value: role.name, label: role.name }))
        .sort((a, b) => a.label.localeCompare(b.label));

  const userKey = (record: User) => record.id || record._id || record.email;

  const handleSelectionChange = (keys: React.Key[], rows: (User | undefined)[]) => {
    rows.forEach((row) => {
      if (row) selectedLabels.current.set(userKey(row), row.name || row.email);
    });
    setSelectedIds(keys.map(String));
  };

  const clearSelection = () => {
    setSelectedIds([]);
    selectedLabels.current.clear();
  };

  const renderRolesForRecord = (_: unknown, record: User) =>
    renderRoleAssignments(record.roleAssignments, roleScopeByName);

  const renderAdminAppAccount = (_: unknown, record: User) =>
    hasNoAdminAppAccount(record) ? greyTag('No account') : renderRolesForRecord(_, record);

  // "No platform users yet" only when nothing but the tab narrows the list (All statuses). On
  // the default Active view an empty list only means nobody active, so it says that instead. Any
  // other filter keeps the table's own "No data". antd shows no empty text during the first load.
  const narrowedOnlyBy = (status: string) =>
    tableFilters.every(
      (f) => 'field' in f && (f.field === 'group' || (f.field === 'status' && f.value === status)),
    );
  let platformEmptyText: string | undefined;
  if (!isServeFirst && narrowedOnlyBy('all')) platformEmptyText = PLATFORM_EMPTY_TEXT;
  else if (!isServeFirst && narrowedOnlyBy(DEFAULT_STATUS)) {
    platformEmptyText = PLATFORM_NO_ACTIVE_TEXT;
  }

  return (
    <List title="Users">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Tabs activeKey={group} items={GROUP_TABS} onChange={handleGroupChange} />
        {/* SF-4875: the search and filters share one row and wrap only when the screen is narrow. */}
        <Space wrap>
          <Input
            placeholder="Search by name or email..."
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{ width: 320 }}
          />
          <Select
            aria-label="Filter by status"
            options={STATUS_FILTER_OPTIONS}
            value={filterValue('status') ?? DEFAULT_STATUS}
            onChange={handleStatusChange}
            style={{ width: 160 }}
          />
          <Select
            aria-label="Filter by team"
            placeholder="Filter by team"
            options={TEAM_FILTER_OPTIONS}
            // null, not undefined: antd treats undefined as uncontrolled and keeps the old choice.
            value={filterValue('team') ?? null}
            onChange={handleTeamChange}
            allowClear
            style={{ width: 240 }}
          />
          <Select
            aria-label="Filter by super admin"
            placeholder="Super admin type"
            options={SUPER_ADMIN_FILTER_OPTIONS}
            value={filterValue('superAdmin') ?? null}
            onChange={handleSuperAdminChange}
            allowClear
            style={{ width: 240 }}
          />
          <Select
            aria-label="Filter by role"
            placeholder="Filter by role"
            options={roleFilterOptions}
            value={filterValue('role') ?? null}
            onChange={handleRoleChange}
            allowClear
            showSearch
            optionFilterProp="label"
            loading={rolesQuery.isLoading}
            notFoundContent={
              rolesQuery.isLoading ? 'Loading roles…' : rolesFailed ? null : 'No roles'
            }
            style={{ width: 240 }}
          />
          <InfiniteCompanySelect
            aria-label="Filter by company"
            placeholder="Filter by company"
            labelInValue
            value={companyFilter ? { value: companyFilter, label: companyLabel } : null}
            onChange={handleCompanyChange}
            allowClear
            queryKey={COMPANY_FILTER_QUERY_KEY}
            fetchPage={fetchCompanyPage}
            enabled={companyPickerUsed}
            onOpenChange={(open) => open && setCompanyPickerUsed(true)}
            style={{ width: 280 }}
          />
          {isServeFirst && (
            <Select
              aria-label="Filter by missing account"
              placeholder="Missing account"
              options={MISSING_ACCOUNT_OPTIONS}
              value={filterValue('missing') ?? null}
              onChange={handleMissingChange}
              allowClear
              style={{ width: 240 }}
            />
          )}
          <ListTotal query={tableQuery} singular="person" plural="people" />
        </Space>
        {rolesFailed && (
          <Alert
            type="warning"
            showIcon
            message="Could not load roles"
            action={
              <Button size="small" onClick={() => rolesQuery.refetch()}>
                Retry
              </Button>
            }
          />
        )}
        {/* Outside the list-error branch: a failed reload must not drop a running batch or its report. */}
        {canManageUsers?.can && (
          <UsersBatchBar
            selectedIds={selectedIds}
            labelFor={(id) => selectedLabels.current.get(id) ?? id}
            roles={rolesFailed ? [] : (rolesResult?.data ?? [])}
            rolesLoading={rolesQuery.isLoading}
            rolesFailed={rolesFailed}
            onChanged={() => tableQuery.refetch()}
            onClear={clearSelection}
          />
        )}
        {/* A failed list is an error, not an empty table that reads as "nobody matches". */}
        {tableQuery.isError ? (
          <Alert
            type="error"
            showIcon
            message="Could not load users"
            description={listErrorMessage(tableQuery.error)}
            action={
              <Button size="small" onClick={() => tableQuery.refetch()}>
                Retry
              </Button>
            }
          />
        ) : (
          <Table
            {...tableProps}
            rowKey={userKey}
            rowSelection={
              canManageUsers?.can
                ? {
                    selectedRowKeys: selectedIds,
                    onChange: handleSelectionChange,
                    // Keep ticks on other pages when moving between pages or filtering.
                    preserveSelectedRowKeys: true,
                    // Team and role changes write to the admin app account, so a person
                    // without one can't be in a batch.
                    getCheckboxProps: (record: User) => ({
                      disabled: hasNoAdminAppAccount(record),
                      title: hasNoAdminAppAccount(record) ? NO_ADMIN_APP_ACCOUNT_TITLE : undefined,
                      'aria-label': `Select ${record.name || record.email}`,
                    }),
                  }
                : undefined
            }
            onRow={(record) =>
              // No admin app account means no person page to open.
              hasNoAdminAppAccount(record)
                ? {}
                : {
                    onClick: (event) => {
                      // Ticking the box (or just missing it) must not open the person.
                      if ((event.target as HTMLElement).closest?.('.ant-table-selection-column'))
                        return;
                      navigate(`/users/${record.id || record._id}`);
                    },
                    style: { cursor: 'pointer' },
                  }
            }
            locale={platformEmptyText ? { emptyText: platformEmptyText } : undefined}
          >
            <Table.Column dataIndex="name" title="Name" sorter />
            <Table.Column dataIndex="email" title="Email" sorter />
            {isServeFirst ? (
              <Table.Column key="adminApp" title="Admin app" render={renderAdminAppAccount} />
            ) : (
              <Table.Column key="roles" title="Role(s)" render={renderRolesForRecord} />
            )}
            {isServeFirst && (
              <Table.Column
                key="webApp"
                dataIndex="platformAccount"
                title="Web app"
                render={renderWebAppAccount}
              />
            )}
            <Table.Column
              dataIndex="team"
              title="Team"
              render={(team: string | null) => teamLabel(team) ?? '—'}
            />
            {companyFilter && (
              <Table.Column
                dataIndex="superAdminAccess"
                title="Access"
                render={(access?: string) => (access && ACCESS_LABELS[access]) || '—'}
              />
            )}
            <Table.Column
              dataIndex="status"
              title="Status"
              render={(status: string) => (
                <Tag color={status === 'active' ? 'green' : 'default'}>{status || 'unknown'}</Tag>
              )}
            />
            <Table.Column
              dataIndex="createdAt"
              title="Created At"
              render={(date: string) => (date ? new Date(date).toLocaleDateString() : '—')}
              sorter
            />
          </Table>
        )}
      </Space>
    </List>
  );
};
